/**
 * @file ContactUs.page.tsx
 * @path apps/website/src/pages/ContactUs.page.tsx
 * @description Master page orchestrating the contact layout via Facade hook.
 */

"use client";

// Core Layout & SEO
import { SeoHelmet as SEOHelmet } from "@/components";
import { NAV_LINKS } from "@/config";

// Assets
import { shuttle3 } from "@/assets/images";
import { video1 } from "@/assets/videos";

// Sections & Components
import { CallToAction, HeroSection } from "@/components/sections";
import { ContactInfoBanner } from "@/features/contact/components/contactInfoBanner.component";
import { ContactForm } from "@/features/contact/components/contactForm.component";
import { FaqSection } from "@/features/contact/components/faqSection.component";
import { ContactMap } from "@/features/contact/components/contactMap.component";

// Facade Hook
import { useContactFacade } from "@/features/contact/hooks/useContactFacade.hook";

const ContactUs = () => {
  // Call the single orchestrator hook
  const facade = useContactFacade();

  return (
    <>
      <SEOHelmet
        pageType="default"
        title="Contact Us | Sir Bastian's Shuttle Service"
        description="Get in touch with Sir Bastians Shuttle Service for bookings, quotes, or inquiries. Call us or fill out our online contact form."
        url={NAV_LINKS.contactUs.path}
      />

      <HeroSection
        videoSrc={video1}
        title="Get In Touch"
        subtitle="We're Here to Help"
        paragraph="Have questions about our services? Need a custom quote? Our team is ready to assist you."
        actions={[
          { text: "Contact Details", scrollTo: "contact-info" },
          { text: "Send an Email", scrollTo: "contact-form" },
        ]}
      />

      {/* --- Main Support Hub Layout --- */}
      <section className="relative py-16 lg:py-24 overflow-hidden bg-background">
        <div className="container px-6 sm:px-10 relative z-10 flex flex-col">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            <div id="contact-info">
              <ContactInfoBanner />
            </div>
            <div id="contact-form" className="h-full col-span-2">
              <ContactForm facade={facade.contactForm} />
            </div>
          </div>

          {/* FAQs */}
          <FaqSection facade={facade.faq} />

          {/* Location Map */}
          <ContactMap />
        </div>
      </section>

      {/* --- Footer CTA --- */}
      <CallToAction
        title="Have a Question? We're Here to Help."
        description="Whether it's a query about our services, a request for a custom quote, or feedback on our trips, we'd love to hear from you."
        primaryAction={{
          text: NAV_LINKS.contactUs.label,
          href: NAV_LINKS.contactUs.path,
        }}
        backgroundImage={{
          src: shuttle3,
          alt: "A friendly driver assisting a traveler with their luggage in Cape Town",
        }}
      />
    </>
  );
};

export default ContactUs;
